import React, { useState, useEffect } from 'react';
import Skeleton from './Skeleton';
import Pagination from './Pagination';
import Checkbox from './Checkbox';

// DataTable v1: listagem + paginação + empty/loading + bulk selection.
// Sort + filtering ficam pra v2 (por enquanto, filtros ficam fora via props).
//
// Props:
//   columns: [{ key, header, render?, width?, align? }]
//   rows: array
//   rowKey?: (row) => string (default: row.id)
//   loading, empty (componente), total, page, pageSize, onPageChange
//   selectable (bool), onSelectionChange (ids), renderBulkActions(ids)
const DataTable = ({
  columns,
  rows = [],
  rowKey = (row) => row.id,
  loading = false,
  empty = null,
  total,
  page,
  pageSize,
  onPageChange,
  selectable = false,
  onSelectionChange,
  renderBulkActions,
  className = '',
  onRowClick,
}) => {
  const [selected, setSelected] = useState(new Set());
  const paginated = total !== undefined && pageSize !== undefined;
  const showEmpty = !loading && rows.length === 0;

  useEffect(() => {
    onSelectionChange?.(Array.from(selected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const isAllSelected = rows.length > 0 && rows.every((r) => selected.has(rowKey(r)));
  const toggleAll = () => {
    setSelected((prev) => {
      if (isAllSelected) return new Set();
      const next = new Set(prev);
      rows.forEach((r) => next.add(rowKey(r)));
      return next;
    });
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  return (
    <div className={className}>
      {selectable && selected.size > 0 && (
        <div className="flex items-center gap-3 p-3 mb-3 bg-primary/5 border border-primary/20 rounded-lg">
          <span className="text-sm font-medium text-primary">
            {selected.size} {selected.size === 1 ? 'selecionado' : 'selecionados'}
          </span>
          <div className="flex-1" />
          {renderBulkActions?.(Array.from(selected), clearSelection)}
          <button
            type="button"
            onClick={clearSelection}
            className="text-sm text-app-text-secondary hover:text-app-text"
          >
            Limpar
          </button>
        </div>
      )}

      <div className="overflow-x-auto border border-app-border rounded-lg">
        <table className="min-w-full divide-y divide-app-border">
          <thead className="bg-app-surface">
            <tr>
              {selectable && (
                <th className="w-10 px-3 py-3">
                  <Checkbox checked={isAllSelected} onChange={toggleAll} />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{ width: col.width, textAlign: col.align || 'left' }}
                  className="px-4 py-3 text-xs font-semibold text-app-text-secondary uppercase tracking-wider whitespace-nowrap"
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-app-border bg-white">
            {loading && (
              <>
                {Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`skel-${i}`}>
                    {selectable && (
                      <td className="px-3 py-3">
                        <Skeleton variant="rect" width={16} height={16} />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td key={col.key} className="px-4 py-3">
                        <Skeleton variant="text" />
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            )}
            {!loading &&
              rows.map((row) => {
                const id = rowKey(row);
                const isSel = selected.has(id);
                return (
                  <tr
                    key={id}
                    className={`hover:bg-app-surface transition-colors ${
                      isSel ? 'bg-primary/5' : ''
                    } ${onRowClick ? 'cursor-pointer' : ''}`}
                    onClick={(e) => {
                      if (e.target.closest('button, input, a')) return;
                      onRowClick?.(row);
                    }}
                  >
                    {selectable && (
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={isSel} onChange={() => toggleOne(id)} />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        style={{ textAlign: col.align || 'left' }}
                        className="px-4 py-3 text-sm text-app-text"
                      >
                        {col.render ? col.render(row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                );
              })}
          </tbody>
        </table>
        {showEmpty && (
          <div className="py-2">
            {empty || (
              <div className="text-center py-10 text-app-text-secondary text-sm">Nenhum item.</div>
            )}
          </div>
        )}
      </div>

      {paginated && total > 0 && (
        <div className="mt-4">
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default DataTable;
